import React, { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryDebugProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryDebugState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundaryDebug extends Component<ErrorBoundaryDebugProps, ErrorBoundaryDebugState> {
  public state: ErrorBoundaryDebugState = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): ErrorBoundaryDebugState {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught error:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-red-50 dark:bg-red-950 p-8">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold text-red-800 dark:text-red-200 mb-4">
              Runtime Error Detected
            </h1>
            <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl p-6 space-y-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">
                  Error Message:
                </h2>
                <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded overflow-x-auto text-sm">
                  {this.state.error && this.state.error.toString()}
                </pre>
              </div>
              
              {this.state.errorInfo && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">
                    Component Stack:
                  </h2>
                  <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded overflow-x-auto text-sm">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </div>
              )}
              
              <div>
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">
                  Stack Trace:
                </h2>
                <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded overflow-x-auto text-sm">
                  {this.state.error && this.state.error.stack}
                </pre>
              </div>
              
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}